import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { getOperatorToken, setOperatorToken, clearOperatorToken, setOperatorUnauthorizedHandler } from "@/api/operator/client";
import { operatorLogin, type OperatorIdentity } from "@/api/operator/auth";

/**
 * Operator-realm auth context — a SEPARATE session from the admin AuthProvider
 * (app/auth.tsx). It stores the operator token in its own key (cogent.operator) and
 * registers the operator 401 handler, so an operator session never collides with a
 * tenant-admin session in the same browser. Mirrors the admin provider's shape.
 */
interface OperatorAuthValue {
  operator: OperatorIdentity | null;
  isAuthenticated: boolean;
  /** True only when a live operator session was ended by a 401 (expired/revoked). */
  sessionExpired: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => void;
}

const OperatorAuthContext = createContext<OperatorAuthValue | null>(null);
const OP_KEY = "cogent.operator";

function readStoredOperator(): OperatorIdentity | null {
  try {
    const raw = sessionStorage.getItem(OP_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as Partial<OperatorIdentity>;
    if (typeof v?.id === "string" && typeof v?.email === "string") {
      return {
        id: v.id,
        email: v.email,
        name: typeof v.name === "string" ? v.name : null,
        active: typeof v.active === "boolean" ? v.active : true,
        createdAt: typeof v.createdAt === "string" ? v.createdAt : "",
        lastLoginAt: typeof v.lastLoginAt === "string" ? v.lastLoginAt : null,
      };
    }
    return null;
  } catch {
    return null;
  }
}

export function OperatorAuthProvider({ children }: { children: ReactNode }) {
  const [operator, setOperator] = useState<OperatorIdentity | null>(() => (getOperatorToken() ? readStoredOperator() : null));
  const [sessionExpired, setSessionExpired] = useState(false);

  const signOut = useCallback(() => {
    clearOperatorToken();
    try {
      sessionStorage.removeItem(OP_KEY);
    } catch {
      /* no-op */
    }
    setSessionExpired(false);
    setOperator(null);
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const res = await operatorLogin(email, password);
    setOperatorToken(res.token);
    try {
      sessionStorage.setItem(OP_KEY, JSON.stringify(res.operator));
    } catch {
      /* no-op */
    }
    setSessionExpired(false);
    setOperator(res.operator);
  }, []);

  useEffect(() => {
    setOperatorUnauthorizedHandler(() => {
      setSessionExpired(true);
      setOperator(null);
    });
    return () => setOperatorUnauthorizedHandler(null);
  }, []);

  const value = useMemo<OperatorAuthValue>(
    () => ({ operator, isAuthenticated: operator !== null, sessionExpired, signIn, signOut }),
    [operator, sessionExpired, signIn, signOut],
  );

  return <OperatorAuthContext.Provider value={value}>{children}</OperatorAuthContext.Provider>;
}

export function useOperatorAuth(): OperatorAuthValue {
  const ctx = useContext(OperatorAuthContext);
  if (!ctx) throw new Error("useOperatorAuth must be used within <OperatorAuthProvider>");
  return ctx;
}
